import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock, Loader2 } from "lucide-react";
import { z } from "zod";

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }).max(255, { message: "Email must be less than 255 characters" }),
  password: z.string().min(1, { message: "Password is required" })
});

const ProducerLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  useEffect(() => {
    checkAuthAndRedirect();
  }, []);

  const checkAuthAndRedirect = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      // Check if user has producer role
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id);

      const hasProducerRole = userRoles?.some((ur: any) => ur.role === 'producer');
      
      if (hasProducerRole) {
        const next = searchParams.get('next') || '/producers/dashboard';
        navigate(next);
      }
    }
  };

  const trackEvent = (eventName: string, properties: Record<string, any> = {}) => {
    // Analytics tracking hook
    console.log(`Analytics: ${eventName}`, properties);
  };

  useEffect(() => {
    trackEvent('auth_login_viewed', {
      path: window.location.pathname,
      referrer: document.referrer,
      variant: 'producer'
    });
  }, []);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});
    setIsLoading(true);

    // Validate inputs
    const validation = loginSchema.safeParse({ email, password });
    if (!validation.success) {
      const errors: { email?: string; password?: string } = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0] === 'email') errors.email = err.message;
        if (err.path[0] === 'password') errors.password = err.message;
      });
      setFieldErrors(errors);
      setIsLoading(false);
      return;
    }

    trackEvent('auth_login_submit', {
      method: 'password',
      has_next: !!searchParams.get('next')
    });

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;

      if (data.user) {
        // Check producer role
        const { data: userRoles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', data.user.id);

        const hasProducerRole = userRoles?.some((ur: any) => ur.role === 'producer');

        if (!hasProducerRole) {
          trackEvent('auth_login_error', {
            method: 'password',
            code: 'no_producer_access'
          });
          setError("Your account doesn't have producer access yet.");
          await supabase.auth.signOut();
          return;
        }

        trackEvent('auth_login_success', { method: 'password' });
        
        const next = searchParams.get('next') || '/producers/dashboard';
        navigate(next);
      }
    } catch (err: any) {
      trackEvent('auth_login_error', {
        method: 'password',
        code: err.message
      });
      setError("Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async () => {
    setError("");
    setFieldErrors({});
    setIsLoading(true);

    // Validate email
    const emailValidation = z.string().trim().email().safeParse(email);
    if (!emailValidation.success) {
      setFieldErrors({ email: "Invalid email address" });
      setIsLoading(false);
      return;
    }

    trackEvent('auth_login_submit', {
      method: 'magic',
      has_next: !!searchParams.get('next')
    });

    try {
      const { error: magicLinkError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/producers/dashboard`,
        }
      });

      if (magicLinkError) throw magicLinkError;

      toast({
        title: "Magic link sent!",
        description: "Check your email for the login link.",
      });

      trackEvent('auth_login_success', { method: 'magic' });
    } catch (err: any) {
      trackEvent('auth_login_error', {
        method: 'magic',
        code: err.message
      });
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Producer Login | SLAY"
        description="Sign in to manage your events, ticketing, and payouts on SLAY."
        ogType="website"
        canonical="https://slay.events/producers/login"
      />
      
      <div className="min-h-screen page-dark flex flex-col">
        <Header />
        
        <main className="flex-grow container mx-auto px-4 pt-28 md:pt-36 pb-12 md:pb-16 flex items-start justify-center">
          <div className="w-full max-w-[420px]">
            {/* Logo and Header */}
            <div className="text-center mb-8 pt-8">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent mb-6">
                Sign in to SLAY Producer
              </h1>
              <p className="text-muted-foreground">
                Manage events, tickets, payouts, and promos.
              </p>
            </div>

            {/* Login Card */}
            <div className="rounded-2xl p-6 md:p-8 shadow-xl bg-card border border-border backdrop-blur-sm">
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                  {error.includes("producer access") && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => navigate('/producers/apply')}
                    >
                      Request Access
                    </Button>
                  )}
                </Alert>
              )}

              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 pr-4 rounded-xl border py-3 text-base focus:ring-2"
                      required
                      disabled={isLoading}
                      autoComplete="email"
                      aria-describedby={fieldErrors.email ? "email-error" : undefined}
                      aria-invalid={!!fieldErrors.email}
                    />
                  </div>
                  {fieldErrors.email && (
                    <p id="email-error" className="text-sm text-destructive mt-1">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                {!showMagicLink && (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-foreground">
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-12 pr-4 rounded-xl border py-3 text-base focus:ring-2"
                        required
                        disabled={isLoading}
                        autoComplete="current-password"
                        aria-describedby={fieldErrors.password ? "password-error" : undefined}
                        aria-invalid={!!fieldErrors.password}
                      />
                    </div>
                    {fieldErrors.password && (
                      <p id="password-error" className="text-sm text-destructive mt-1">
                        {fieldErrors.password}
                      </p>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full rounded-xl px-4 py-3 font-medium"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              {showMagicLink ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full rounded-xl"
                  onClick={handleMagicLink}
                  disabled={isLoading || !email}
                >
                  Send Magic Link
                </Button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowMagicLink(true)}
                  className="w-full text-sm text-primary hover:underline"
                >
                  Email me a magic link
                </button>
              )}

              <div className="mt-6 space-y-2 text-center text-sm">
                <Link
                  to="/reset-password"
                  className="text-primary hover:underline block"
                >
                  Forgot password?
                </Link>
                <Link
                  to="/producers/apply"
                  className="text-muted-foreground hover:text-foreground block"
                >
                  Contact SLAY for producer access
                </Link>
              </div>

              <p className="mt-6 text-xs text-center text-muted-foreground">
                By continuing, you agree to SLAY's{' '}
                <Link to="/terms-of-service" className="text-primary hover:underline">
                  Terms
                </Link>{' '}
                and{' '}
                <Link to="/privacy-policy" className="text-primary hover:underline">
                  Privacy
                </Link>
                .
              </p>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default ProducerLogin;
